import React, { useCallback, useContext, useRef, useState } from "react";
import { A11y, Autoplay, Navigation, Pagination } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";

import { Button } from "../../components/Button";
import { HeroText } from "../HeroText/HeroText";
import { SectionContainer } from "../../components/SectionContainer";
import { StructuredTextRenderer } from "../../components/StructuredTextRenderer";
import { TeaserCard } from "../../components/teaser-components/TeaserCard/TeaserCard";

import { generateClassString as gcs } from "../../lib/helper/generateClassString";
import {
    useIsMobileLandscape,
    useIsSmaller1400
} from "../../lib/hooks/mediaQueries";
import ElementContext from "../../components/ElementContext";
import GeneralPageContext from "../../templates/GeneralPageContext";
import t from "../../lib/translate";

import * as styles from "./component.module.less";
import { sectionId as cameraListingSectionId } from "../../page-components/camera/CameraListing/CameraListing";

const idButtonPrev = "header-teaser-prev";
const idButtonNext = "header-teaser-next";

function HeroTeaser({
    data: {
        semanticOfHeadline,
        tagline,
        subheadline,
        headline,
        semanticOfTeaserHeadline,
        ctaButton,
        teaser,
        background,
        TypeSpecificContent,
        gridSize = "s"
    },
    type,
    children
}) {
    const isSmaller1400 = useIsSmaller1400();
    const isMobileLandscape = useIsMobileLandscape();
    const { isLightBg } = useContext(ElementContext);

    const {
        layout: { showBreadcrumb }
    } = useContext(GeneralPageContext);

    const fixedButtonContent = (link, filterLink) => (
        <div
            className={gcs(
                styles.heroTeaserButtonBlock,
                !isLightBg ? styles.lightColor : ""
            )}
        >
            <StructuredTextRenderer
                data={link}
                itemLinkType="secondary"
                itemLinkIcon="arrowRight"
                itemLinkIconPosition="right"
                contentElementType={type}
            />
            <StructuredTextRenderer
                data={filterLink}
                itemLinkType="secondary"
                itemLinkIcon="arrowRight"
                itemLinkIconPosition="right"
                contentElementType={type}
                urlFragment={cameraListingSectionId}
            />
        </div>
    );

    return (
        <SectionContainer
            sectionClass={gcs(styles.heroTeaserSection, [
                showBreadcrumb,
                styles.noOuterSectionPadding
            ])}
            containerClass={styles.heroTeaserContainer}
            reducedWidth="never"
            background={background}
        >
            <HeroText
                className={styles.heroTeaserTextBlock}
                data={{
                    semanticOfHeadline,
                    tagline,
                    subheadline,
                    headline,
                    background
                }}
            >
                <StructuredTextRenderer
                    data={ctaButton}
                    itemLinkType="cta"
                    contentElementType={type}
                />
            </HeroText>

            <div
                className={gcs(
                    styles.sliderControls,
                    !isLightBg ? styles.lightColor : ""
                )}
            >
                <Button
                    id={idButtonPrev}
                    type="prev"
                    icon="arrowLeft"
                    aria-label={t("navigation.button.prev")}
                />

                <Button
                    id={idButtonNext}
                    type="next"
                    icon="arrowRight"
                    aria-label={t("navigation.button.next")}
                />
            </div>
            <div className={styles.heroTeaserSwiperBlock}>
                <Swiper
                    className={styles.heroTeaserSwiper}
                    modules={[Navigation, Pagination, Autoplay, A11y]}
                    slidesPerView={
                        isMobileLandscape ? 1.125 : isSmaller1400 ? 3.5 : 4.5
                    }
                    spaceBetween={isSmaller1400 ? 30 : 60}
                    navigation={{
                        prevEl: `#${idButtonPrev}`,
                        nextEl: `#${idButtonNext}`
                    }}
                    autoplay={isMobileLandscape ? false : {delay: 5000}}
                >
                    {teaser.map((teaserElement) => {
                        const {
                            teaserImage: image,
                            teaserTagline: tagline,
                            teaserHeadline: headline,
                            division,
                            teaserText: text,
                            teaserLink: link,
                            teaserFilterLink: filterLink,
                            url
                        } = teaserElement;

                        return (
                            <SwiperSlide key={teaserElement.id}>
                                <TeaserCard
                                    cardClass={styles.heroTeaserCard}
                                    hasLightBackground={isLightBg}
                                    key={teaserElement.id}
                                    hideButtonLabel={false}
                                    FixedContent={fixedButtonContent(
                                        link,
                                        filterLink
                                    )}
                                    teaserData={{
                                        image,
                                        tagline,
                                        headline,
                                        semanticOfHeadline:
                                            semanticOfTeaserHeadline,
                                        division,
                                        text,
                                        url,
                                        gridSize: gridSize
                                    }}
                                >
                                    {TypeSpecificContent && (
                                        <TypeSpecificContent
                                            {...teaserElement}
                                        />
                                    )}
                                </TeaserCard>
                            </SwiperSlide>
                        );
                    })}
                </Swiper>
            </div>
            {children}
        </SectionContainer>
    );
}

export { HeroTeaser };
